'use client';

import type { MouseEvent } from 'react';
import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { resolveAiComponentDisplayDescription } from '@terra/shared-contracts-ai-applications';
import {
  Badge,
  Card,
  EntityIcon,
  Text,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { AI_COMPONENT_TYPE_KEYS } from '../utils/aiComponentTypeLabels';
import {
  AI_COMPONENT_TYPE_ICON_COLORS,
  getAiComponentTypeIcon,
} from '../utils/aiComponentIcons';
import {
  formatMcpIdentityAccessTooltipContent,
  getMcpComponentIdentityAccessSummary,
  isMcpAssetComponentType,
} from '../utils/aiComponentMetadata';
import { ComponentDetectionMetric } from './ComponentDetectionMetric';

type AiApplicationAssetCardProps = {
  component: AiComponentDto;
  signalsCount: number;
  findingsCount: number;
  isSignalsUnavailable?: boolean;
  isFindingsUnavailable?: boolean;
  onClick?: () => void;
  onContextMenu?: (event: MouseEvent<HTMLElement>) => void;
};

export function AiApplicationAssetCard({
  component,
  signalsCount,
  findingsCount,
  isSignalsUnavailable = false,
  isFindingsUnavailable = false,
  onClick,
  onContextMenu,
}: AiApplicationAssetCardProps) {
  const t = useTranslations('aiApplications.assets');
  const tMcp = useTranslations('aiApplications.componentDrawer.mcp');
  const tComponentTypes = useTranslations('aiApplications.componentTypes');
  const Icon = getAiComponentTypeIcon(component.type);
  const iconColors = AI_COMPONENT_TYPE_ICON_COLORS[component.type];
  const mcpAccessSummary = isMcpAssetComponentType(component.type)
    ? getMcpComponentIdentityAccessSummary({ metadata: component.metadata })
    : null;
  const mcpAccessLabels = {
    accessDenied: tMcp('notListed'),
    call: tMcp('accessCall'),
  };
  const identityAccessTooltip = isMcpAssetComponentType(component.type)
    ? formatMcpIdentityAccessTooltipContent({
        metadata: component.metadata,
        labels: mcpAccessLabels,
      })
    : undefined;
  const showAccessBadge =
    mcpAccessSummary !== null &&
    mcpAccessSummary.totalCount >= 2 &&
    mcpAccessSummary.deniedCount > 0;
  const isSuppressed = component.verificationStatus === 'SUPPRESSED';

  const displayDescription = resolveAiComponentDisplayDescription({
    description: component.description,
    metadata: component.metadata,
    type: component.type,
  });

  return (
    <Card.Root
      w="full"
      borderWidth="1px"
      borderColor="border.subtle"
      cursor={onClick ? 'pointer' : 'default'}
      _hover={onClick ? { bg: 'bg.hover' } : undefined}
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <Card.Body p="3">
        <VStack gap="2" alignItems="stretch">
          <HStack justifyContent="space-between" alignItems="center" gap={2}>
            <HStack gap={2} alignItems="center" minW={0}>
              <Box
                bg={iconColors.bg}
                borderWidth="1px"
                borderColor={iconColors.border}
                color={iconColors.color}
                borderRadius="sm"
                p={1}
                flexShrink={0}
              >
                <Icon size={16} />
              </Box>
              <Text fontSize="sm" fontWeight="medium" color="fg.default">
                {tComponentTypes(AI_COMPONENT_TYPE_KEYS[component.type])}
              </Text>
            </HStack>
            <HStack gap={4} alignItems="center" flexShrink={0}>
              <ComponentDetectionMetric
                icon={
                  <EntityIcon
                    type="signal"
                    appearance="default"
                    density="compact"
                  />
                }
                label={t('signals')}
                count={signalsCount}
                isUnavailable={isSignalsUnavailable}
              />
              <ComponentDetectionMetric
                icon={
                  <EntityIcon
                    type="finding"
                    appearance="default"
                    density="compact"
                  />
                }
                label={t('findings')}
                count={findingsCount}
                isUnavailable={isFindingsUnavailable}
              />
            </HStack>
          </HStack>

          <VStack gap={1} alignItems="stretch">
            <Text fontSize="sm" fontWeight="medium" lineClamp={2}>
              {component.name}
            </Text>
            {displayDescription ? (
              <Text fontSize="xs" color="fg.muted" lineClamp={2}>
                {displayDescription}
              </Text>
            ) : null}
          </VStack>

          <HStack gap={2} alignItems="center" flexWrap="wrap">
            {isSuppressed ? (
              <Badge colorPalette="gray">{t('verification.suppressed')}</Badge>
            ) : null}
            {showAccessBadge && mcpAccessSummary ? (
              <Tooltip
                title={identityAccessTooltip}
                whiteSpace="pre-line"
                disabled={!identityAccessTooltip}
              >
                <Badge colorPalette="orange">{t('accessSomeIdentities')}</Badge>
              </Tooltip>
            ) : null}
            <Badge colorPalette={component.isInternal ? 'gray' : 'blue'}>
              {component.isInternal ? t('internal') : t('external')}
            </Badge>
          </HStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}
